/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Form, Text } from 'tea-component';
import DragableUploader from '@gp/components/DragableUploader';
import CoverCard from '@gp/components/CoverUploader/components/CoverCard';
import useCos from '@gp/hooks/useCos';
import { file2DataURL } from '@home/utils/file';
import { openFileDialog, validateUploadFilesWithErrorNotice } from '@gp/utils/file';
import { DEFAULT_TIPS } from '@gp/components/CoverUploader/constants';
import { UPLOAD_FOLDER_NAME_MAP } from '@gp/constants/const';
import type { IBaseProps } from '@home/types/common';
import type { IGeneralImageDomain } from '@gp/components/GeneralImageNew';
import type { IUploadStatus, ICover } from '@gp/components/CoverUploader/types';

import './index.scss';

// CoverUpload组件实例
export interface ICoverUploaderInstance {
  reset: () => void;
}

interface ICoverUploaderProps extends IBaseProps {
  // domain，比如说user、game、studio，不同的domain有不同的默认图片
  domain?: IGeneralImageDomain;
  // 图片唯一标识key
  uniqueKey?: string;
  // 上传文件类型限制
  accept?: string[];
  // 上传文件大小限制
  fileSize?: number;
  // 默认图片
  errorImg?: string;
  // 文件类型、文件大小限制这部分的提示内容
  tips?: string | React.ReactElement;
  value?: any;
  // 通知组件调用者，回传最新cover元信息
  onUploadSuccess?: (payload: ICover) => void;
}

const ACCEPT = ['image/png', 'image/jpeg'];
const LIMIT_SIZE = 1024 * 1024 * 2;
const PLACEHOLDER_COVER = '//imgcache.qq.com/open_proj/proj_qcloud_v2/tea-style/dist/assets/image/upload-thumb.png';

const EMPTY_META = {
  name: '',
  cover: '',
  file_size: Number.NaN,
  timestamp: Number.NaN,
};

const JOINER = '@@';

const formatFileName = (name?: string, uniqueKey?: string): string => {
  if (!name) return '';
  if (!uniqueKey) return name;
  return `${uniqueKey}${JOINER}${name}`;
};

const analysisFileName = (name?: string, uniqueKey?: string): string => {
  if (!name) return '';
  if (!uniqueKey) return name;
  return name.replace(`${uniqueKey}${JOINER}`, '');
};

const CoverUploader: React.ForwardRefRenderFunction<ICoverUploaderInstance, ICoverUploaderProps> = (props, ref) => {
  const {
    style,
    className,
    domain,
    uniqueKey,
    accept = ACCEPT,
    fileSize = LIMIT_SIZE,
    errorImg = PLACEHOLDER_COVER,
    tips,
    value,
    onUploadSuccess,
  } = props;

  const uploadFolderName = UPLOAD_FOLDER_NAME_MAP.image;

  const { formatMessage } = useIntl();

  const { putObject, getObjectUrl, cancelTask } = useCos({qCouldServiceParams: uploadFolderName});

  // cover的dataURL，用于页面展示
  const [cover, setCover] = useState('');
  // cover的元信息，其中cover是保存COS返回的图片访问链接，需要回传给组件调用者
  const [meta, setMeta] = useState<ICover>(EMPTY_META);
  /**
   * 上传状态
   * none 空（代表没上传，空态）
   * uploading 正在上传中（已读取完本地文件，可以用dataURL形式展示图片）
   * success 已上传到服务器
   * error 上传失败
   */
  const [status, setStatus] = useState<IUploadStatus>('none');
  // 上传进度
  const [percents, setPercents] = useState(Number.NaN);
  // 上传任务id
  const taskId = useRef('');

  // 上传进度更新处理
  const handleProgress = ({ percent }: { percent: number; }) => {
    const percentsTmp = Math.round(percent * 100);
    setPercents(percentsTmp);
  };

  // 重新上传
  const handleUploadAgain = async () => {
    const files = await openFileDialog({
      multiple: false,
      accept: accept?.join(','),
    });
    // @ts-ignore
    const isValid = validateUploadFilesWithErrorNotice(files, formatMessage, {
      multiple: false,
      max: 1,
      accept,
      limitSize: fileSize,
    });
    // 重新上传 文件校验失败时，不清空已有图片 展示错误图片
    if (!isValid) return;
    // 重新上传 文件校验成功，并且有正在上传中的图片时，需要取消正在上传的图片
    if (taskId.current) {
      cancelTask(taskId.current);
      taskId.current = '';
    }
    submit(files);
  };

  // 重置内部状态
  const handleReset = () => {
    setCover('');
    setMeta(EMPTY_META);
    setPercents(Number.NaN);
    setStatus('none');
  };

  // 取消上传
  const handleCancel = () => {
    if (taskId.current) {
      cancelTask(taskId.current);
      taskId.current = '';
    }
    handleDelete();
  };

  // 删除
  const handleDelete = () => {
    handleReset();
    onUploadSuccess?.(EMPTY_META);
  };

  // 图片校验失败的逻辑
  const handleValidateError = (files: File[]) => {
    if (!Array.isArray(files) || !files.length) return;
    const { name, size } = files[0];
    const metaTmp = {
      name,
      cover: '',
      file_size: size,
      timestamp: new Date().getTime(),
    };
    setCover(errorImg);
    setMeta(metaTmp);
    setStatus('error');
  };

  // 图片通过校验，上传COS
  const submit = async (files: File[]) => {
    if (!Array.isArray(files) || !files.length) return;
    let metaTmp: ICover = {
      name: '',
      cover: '',
      file_size: Number.NaN,
      timestamp: Number.NaN,
    };
    try {
      const file = files[0];
      const { name, size } = file;
      // 解析本地图片，获取File对象
      const fileDataUrl = await file2DataURL(file);
      metaTmp = {
        ...meta,
        name: formatFileName(name, uniqueKey),
        file_size: size,
        timestamp: new Date().getTime(),
      };
      setCover(fileDataUrl);
      setMeta(metaTmp);
      setStatus('uploading');
      const path = `image/${metaTmp.name}`;
      // 上传到COS
      await putObject({
        path,
        file,
        onProgress: handleProgress,
        onTaskReady: (id: string) => {
          taskId.current = id;
        },
      });
      // 从COS获取刚上传的图片的链接
      const res = await getObjectUrl({ path });
      metaTmp = { ...metaTmp, cover: res?.url || '' };
      setMeta(metaTmp);
      setStatus('success');
      onUploadSuccess?.(metaTmp);
    } catch (exception) {
      metaTmp = { ...metaTmp, cover: '' };
      setCover(errorImg);
      setMeta(metaTmp);
      setStatus('error');
      onUploadSuccess?.(EMPTY_META);
    } finally {
      setPercents(Number.NaN);
      taskId.current = '';
    }
  };

  useEffect(() => {
    const { name, cover: coverValue } = value || {};
    if (!!name && !!coverValue) {
      const metaTmp = { ...meta, ...value };
      if (!cover) {
        setCover(coverValue);
      }
      setMeta(metaTmp);
      setPercents(Number.NaN);
      setStatus('success');
    }
  }, [value]);

  useImperativeHandle(ref, () => ({
    reset: handleReset,
  }));

  return (
    <section style={style} className={classnames('dbg-cover-uploader gp-form-upload-content font14', className)}>
      <Form.Control className={classnames({ 'form-upload-content-error': status === 'error' })}>
        {status !== 'none' && (
          <CoverCard
            domain={domain}
            errorImg={errorImg}
            status={status}
            percents={percents}
            tips={tips}
            cover={cover}
            data={{ ...meta, name: analysisFileName(meta?.name, uniqueKey) }}
            onCancel={handleCancel}
            onUploadAgain={handleUploadAgain}
            onDelete={handleDelete}
          />
        )}
        {status === 'none' && (
          <DragableUploader
            className="dbg-cover-uploader__uploader"
            accept={accept}
            limitSize={fileSize}
            onValidateError={handleValidateError}
            onSelect={submit}
            // @ts-ignore
            footer={null}
            placeholder={
              <div className="dbg-cover-uploader__placeholder">
                <a>Click to Upload</a>
                <span style={{ margin: '0 2px' }}>/</span>
                <Text theme="text">Drag to this area</Text>
                <p className="dbg-cover-uploader__placeholder-tips">{DEFAULT_TIPS}</p>
              </div>
            }
            placeholderWhenDragging={<div className="dbg-flex-center" style={{ 'fontSize': '14px' , position: 'relative', top: '4px'}}>Mouse Up</div>}
          />
        )}
      </Form.Control>
    </section>
  );
};

export default React.forwardRef(CoverUploader);
